import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import NorthEast from "@mui/icons-material/NorthEast";
import ArrowUpIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownIcon from "@mui/icons-material/ArrowDownward";
import Avatar from "@mui/material/Avatar";
import { icons } from "constants/icons";
import { CircularProgress } from "@material-ui/core";
import { capitalize } from "utils/formatters";
import moment from "moment";
import { getTeamsString } from "repositories/teamsApi";

const valueSpan = {
  maxWidth: "50px",
  minWidth: "50px",
  marginLeft: "5px",
  marginRight: "5px",
};
const letterSpan = {
  maxWidth: "15px",
  minWidth: "15px",
};

interface Athlete {
  _id: string;
  firstName: string;
  lastName: string;
  avatar: string;
  rank: string;
  age: number;
  teams: string[];
  positions: string[];
  sports: string[];
  kneeMetric: number;
  lastExam: string;
  calculations: {
    currentAverage: number;
    previousAverage: number;
    averagesDiffPercentage: number;
    hasImproved: boolean;
  };
  leftSideData: {
    current: {
      leftMinValue: number;
    };
    previous: {
      leftMinValue: number;
    };
  };
  rightSideData: {
    current: {
      rightMinValue: number;
    };
    previous: {
      rightMinValue: number;
    };
  };
  bothSideData: {
    current: {
      leftMinValue: number;
      rightMinValue: number;
    };
    previous: {
      leftMinValue: number;
      rightMinValue: number;
    };
  };

  patient: {
    _id: string;
    imageUrl: string;
    profileInfo: {
      firstName: string;
      lastName: string;
      dateOfBirth: string;
      sports: string[];
      teams: string[];
      gender: string;
      sportPosition: string[];
    };
  };
}

const formatDecimal = (
  number: number,
  decimal: number,
  defaultValue?: string
) => {
  if (number === undefined) return "N/A";
  if (defaultValue) {
    if (number === null) return "N/A";
    if (number === 0) return "N/A";
  }
  return Number(number).toFixed(decimal);
};

const renderValues = (
  current: number,
  previous: number,
  difference: number,
  hasImproved: boolean
) => {
  return (
    <div className="text-xl font-bold flex items-center gap-1">
      <strong style={valueSpan}>{formatDecimal(current, 1)}</strong>
      {" | "}
      <strong style={valueSpan}>{formatDecimal(previous, 1, "N/A")}</strong>
      {" | "}
      <div
        className={`
        inline-flex items-center gap-1 px-3 py-1 rounded-full
        ${hasImproved ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}
      `}
      >
        <span className="font-semibold">{formatDecimal(difference, 0)}%</span>
        {hasImproved ? (
          <ArrowUpIcon className="w-4 h-4" />
        ) : (
          <ArrowDownIcon className="w-4 h-4" />
        )}
      </div>
    </div>
  );
};

const AthleteRanking = ({
  loading,
  athleteList,
  teams,
  label,
  section,
}: {
  loading: boolean;
  athleteList: Athlete[];
  teams: any[];
  label: string;
  section: string;
}) => {
  const history = useHistory();

  const handleAthleteClick = (athleteId: string): void => {
    history.push(`/patient-details/${athleteId}?from=leaderboard`);
  };

  const getYearsSince = (isoString: string) => {
    const startDate = moment(isoString);
    const now = moment();
    return now.diff(startDate, "years");
  };
  return (
    <>
      <h3 className="text-2xl font-bold mb-6">{label}</h3>

      {loading && (
        <div
          style={{
            height: "100vh",
            width: "100vw",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {" "}
          <CircularProgress />
        </div>
      )}

      {!loading && athleteList.length > 0 && (
        <div className="flex flex-nowrap gap-3 overflow-x-auto pb-4">
          {athleteList.map((athlete: Athlete, index: number) => (
            <div
              key={athlete.patient._id}
              className="bg-white p-4 rounded-lg shadow min-w-[calc(25%-16px)] flex-1"
            >
              <div className="flex flex-col">
                <div className="flex items-start gap-4 mb-4">
                  {athlete.patient.imageUrl ? (
                    <Avatar
                      src={athlete.patient.imageUrl}
                      sx={{
                        width: 80,
                        height: 80,
                      }}
                    />
                  ) : (
                    <img
                      width={80}
                      height={80}
                      src={icons.profile}
                      alt="User"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg text-gray-600">
                        #{athlete.rank || index + 1}
                      </span>
                    </div>

                    <div className="space-y-0.5">
                      {" "}
                      {/* Reduced space between name lines */}
                      <h3 className="text-xl font-bold leading-tight truncate">
                        {athlete.patient.profileInfo.firstName}
                      </h3>
                      <h3 className="text-xl font-bold leading-tight truncate">
                        {athlete.patient.profileInfo.lastName}
                      </h3>
                      <div className="text-base text-gray-500 mt-1">
                        <strong>
                          {capitalize(athlete.patient.profileInfo.gender)}
                        </strong>
                        {"  "}(Age:{" "}
                        {getYearsSince(athlete.patient.profileInfo.dateOfBirth)}
                        )
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 min-w-0">
                  {" "}
                  <div className="flex justify-between items-start gap-4">
                    <div className="space-y-2 min-w-0 flex-1">
                      <div
                        className="text-sm text-gray-600 underline cursor-pointer truncate"
                        onClick={() => handleAthleteClick(athlete._id)}
                        title={
                          athlete.patient.profileInfo.sports?.join(", ") ||
                          "Sport not assigned"
                        }
                      >
                        {athlete.patient.profileInfo.sports?.join(", ") ||
                          "Sport not assigned"}
                      </div>
                      <div
                        className="text-sm text-gray-600 underline cursor-pointer truncate"
                        title={
                          getTeamsString(
                            athlete.patient.profileInfo.teams,
                            teams
                          ) || "Team not assigned"
                        }
                      >
                        {getTeamsString(
                          athlete.patient.profileInfo.teams,
                          teams
                        ) || "Team not assigned"}
                      </div>
                      <div
                        className="text-sm text-gray-600 underline cursor-pointer truncate"
                        title={
                          athlete.patient.profileInfo.sportPosition?.length
                            ? athlete.patient.profileInfo.sportPosition?.join(
                                ", "
                              ) || "Position not assigned"
                            : "Position not assigned"
                        }
                      >
                        {athlete.patient.profileInfo.sportPosition?.length
                          ? athlete.patient.profileInfo.sportPosition?.join(
                              ", "
                            ) || "Position not assigned"
                          : "Position not assigned"}
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg text-center min-w-[120px]">
                      <div className="text-sm text-gray-500 mb-1">Min avg</div>
                      <div
                        className={`text-3xl font-bold text-${
                          athlete.calculations.hasImproved ? "green" : "red"
                        }-500`}
                      >
                        {athlete.calculations.currentAverage || "N/A"}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500 font-extrabold">
                      Min avg of last & previous exams
                    </div>
                    {renderValues(
                      athlete.calculations.currentAverage,
                      athlete.calculations.previousAverage,
                      athlete.calculations.averagesDiffPercentage,
                      athlete.calculations.hasImproved
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg flex justify-between items-stretch">
                    <div className="py-4 px-6">
                      <div className="text-gray-600 text-lg">Last exam</div>
                      <div className="text-2xl font-bold mt-1">
                        {moment(athlete.lastExam).format("MM/DD/YY")}
                      </div>
                    </div>
                    <button
                      className="bg-gray-900 rounded-r-lg w-20 flex items-center justify-center hover:bg-gray-800"
                      onClick={() => handleAthleteClick(athlete._id)}
                    >
                      <NorthEast className="w-5 h-5" sx={{ fill: "#fff" }} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && athleteList.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 max-w-xl text-center shadow-sm">
            <img
              src={icons.empty}
              alt="No eligible athletes"
              className="mx-auto mb-4 h-20 w-20 opacity-80"
            />
            <h4 className="text-xl font-semibold mb-2">
              No {section} eligible athletes yet
            </h4>
            <p className="text-gray-600">
              Only athletes with more than 2 rounds of 3 exams are eligible for
              the leaderboard.
            </p>
            <div className="mt-4 text-sm text-gray-500">
              Tip: Encourage athletes to complete more exam rounds to appear
              here.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AthleteRanking;
